import { request } from '@/api/request'

/** Mirrors Prisma `SlaPolicy`. */
export interface SlaPolicy {
  id: string
  name: string
  priority: string
  firstResponseMins: number
  resolutionMins: number
  calendarId: string
  pauseStatuses: string[]
  warnAtPct: number
  customerId: string | null
  productId: string | null
  active: boolean
  calendar?: { id: string; name: string } | null
  customer?: { id: string; name: string } | null
  product?: { id: string; key: string; name: string } | null
  createdAt: string
  updatedAt: string
}

export type SlaPolicyInput = Omit<SlaPolicy, 'id' | 'createdAt' | 'updatedAt' | 'calendar' | 'customer' | 'product'>

/** `{ mon: [["09:00","17:00"]], …, sun: [] }`. */
export type WeekHours = Record<string, [string, string][]>

/** Mirrors Prisma `BusinessCalendar`. */
export interface BusinessCalendar {
  id: string
  name: string
  /** IANA zone, or `customer` = the ticket's customer time zone. */
  timeZone: string
  hours: WeekHours
  holidays: string[]
  is24x7: boolean
  createdAt: string
  updatedAt: string
}

export type BusinessCalendarInput = Omit<BusinessCalendar, 'id' | 'createdAt' | 'updatedAt'>

/**
 * Mirrors Prisma `RoutingRule`. Conditions: `{ products?, components?, types?,
 * priorities?, customerIds?, channels?, subjectContains? }`; actions:
 * `{ teamId?, assigneeId?, priority?, addWatcherContactIds? }`.
 */
export interface RoutingRule {
  id: string
  order: number
  active: boolean
  name: string
  conditions: unknown
  actions: unknown
  createdAt: string
  updatedAt: string
}

export type RoutingRuleInput = Omit<RoutingRule, 'id' | 'createdAt' | 'updatedAt'>

/** Mirrors Prisma `AdminAuditEntry`. */
export interface AdminAuditEntry {
  id: string
  entityType: string
  entityId: string
  action: string
  before: unknown
  after: unknown
  reason: string | null
  actorId: string
  actorName?: string | null
  at: string
}

/** List endpoints may answer a bare array or a `{ items }` page; normalize to an array. */
async function list<T>(path: string): Promise<T[]> {
  const res = await request<T[] | { items: T[] }>(path)
  return Array.isArray(res) ? res : res.items
}

function crud<T, I>(base: string) {
  const enc = encodeURIComponent
  return {
    list: (): Promise<T[]> => list<T>(base),
    create: (input: I): Promise<T> => request<T>(base, { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: Partial<I>): Promise<T> =>
      request<T>(`${base}/${enc(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string): Promise<void> => request<void>(`${base}/${enc(id)}`, { method: 'DELETE' }),
  }
}

export const adminApi = {
  slaPolicies: crud<SlaPolicy, SlaPolicyInput>('/sla-policies'),
  calendars: crud<BusinessCalendar, BusinessCalendarInput>('/calendars'),
  routingRules: crud<RoutingRule, RoutingRuleInput>('/routing-rules'),
  adminAudit: (): Promise<AdminAuditEntry[]> => list<AdminAuditEntry>('/admin-audit'),
}
